import { 
    Card, Grid, Icon, IconButton, Switch, Autocomplete, Chip 
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import { categoriaOcorrenciaEnum } from "utils/Enums";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";

export default function TipoOcorrenciaCreateEdit({ id, closeCreateEdit }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const { userPerfils } = useUser();
    const isAdminOrSuporte = userPerfils && userPerfils.some(p => {
        const pName = (p?.nome || (typeof p === 'string' ? p : "")).toUpperCase();
        return p?.tipoPerfil === 4 || p?.tipoPerfil === 6 || pName === 'ADMINISTRADOR' || pName === 'SUPORTE';
    });

    const availableCategories = isAdminOrSuporte ? categoriaOcorrenciaEnum : categoriaOcorrenciaEnum.filter(c => c.code === 1);

    const [initialValues, setInitialValues] = useState({
        id: null,
        nome: "",
        cor: "success",
        categoria: 1,
        ativo: true,
        equipesIds: [],
        prazosEquipes: {},
        statusFichaIds: [],
        permiteAgendamento: false,
        prazoMaxAgendamento: 0
    });

    const [grupos, setGrupos] = useState([]);
    const [statuses, setStatuses] = useState([]);

    useEffect(() => {
        get("/equipe/list-all").then((res) => {
            setGrupos(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        get("/status-ficha/list-all").then((res) => {
            setStatuses(res.list || res.data || (Array.isArray(res) ? res : []));
        });

        if (id) {
            get(`/tipo-ocorrencia/by-id/${id}`).then((res) => {
                if (res) {
                    setInitialValues({
                        ...res,
                        categoria: res.categoria || 1,
                        equipesIds: res.equipesIds || [],
                        prazosEquipes: res.prazosEquipes || {},
                        statusFichaIds: res.statusFichaIds || []
                    });
                }
            });
        }
    }, [id]);

    const handleSubmit = (values, { setSubmitting }) => {
        postNoResponse("/tipo-ocorrencia/save", values)
            .then(() => {
                successAlert("Tipo de Ocorrência salvo com sucesso!", () => closeCreateEdit());
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <Card id="tipo-ocorrencia-form">
            <MDBox display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1 }}>
                    <MDTypography variant="h5"><Icon>arrow_back</Icon></MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar Tipo de Ocorrência" : "Novo Tipo de Ocorrência"}</MDTypography>
            </MDBox>

            <MDBox component="section" px={3} pb={3}>
                <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
                    {({ values, handleChange, setFieldValue, isSubmitting }) => {
                        const selectedGrupos = grupos.filter(g => (values.equipesIds || []).includes(g.id));
                        const showEquipesPrazos = values.permiteAgendamento && selectedGrupos.length > 1;

                        return (
                            <Form>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <MDInput fullWidth name="nome" label="Nome" variant="standard" value={values.nome || ""} onChange={handleChange} required />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Autocomplete
                                            options={availableCategories}
                                            getOptionLabel={(option) => option.label}
                                            value={availableCategories.find(c => c.code === values.categoria) || availableCategories[0]}
                                            onChange={(event, newValue) => {
                                                const catCode = newValue ? newValue.code : 1;
                                                let newCor = "success";
                                                if (catCode === 2) newCor = "info";
                                                if (catCode === 3) newCor = "warning";
                                                if (catCode === 4) newCor = "error";
                                                if (catCode === 5) newCor = "secondary";
                                                setFieldValue("categoria", catCode);
                                                setFieldValue("cor", newCor);
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Categoria" variant="standard" required />}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Autocomplete
                                            options={[
                                                { code: null, label: "Nenhuma (Sem alteração)" },
                                                { code: 1, label: "Devolvido" },
                                                { code: 2, label: "Cobrança" },
                                                { code: 3, label: "Negociação" },
                                                { code: 4, label: "Pesquisa" },
                                                { code: 5, label: "Suspenso" },
                                                { code: 6, label: "Migrado" },
                                                { code: 7, label: "Quitado" }
                                            ]}
                                            getOptionLabel={(option) => option.label || ""}
                                            value={[
                                                { code: null, label: "Nenhuma (Sem alteração)" },
                                                { code: 1, label: "Devolvido" },
                                                { code: 2, label: "Cobrança" },
                                                { code: 3, label: "Negociação" },
                                                { code: 4, label: "Pesquisa" },
                                                { code: 5, label: "Suspenso" },
                                                { code: 6, label: "Migrado" },
                                                { code: 7, label: "Quitado" }
                                            ].find(s => s.code === values.situacaoFicha) || { code: null, label: "Nenhuma (Sem alteração)" }}
                                            onChange={(event, newValue) => {
                                                setFieldValue("situacaoFicha", newValue ? newValue.code : null);
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Altera Situação da Ficha para" variant="standard" />}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3} display="flex" alignItems="center" pt={4} pl={2}>
                                        <MDTypography variant="button" fontWeight="regular" color="text">Inativo</MDTypography>
                                        <Switch checked={values.ativo} onChange={(e) => setFieldValue("ativo", e.target.checked)} />
                                        <MDTypography variant="button" fontWeight="regular" color="text">Ativo</MDTypography>
                                    </Grid>
                                    <Grid item xs={12} sm={4} display="flex" alignItems="center" pt={4} pl={2}>
                                        <Switch checked={values.permiteAgendamento} onChange={(e) => setFieldValue("permiteAgendamento", e.target.checked)} />
                                        <MDTypography variant="button" fontWeight="regular" color="text">Permite Agendamento?</MDTypography>
                                    </Grid>
                                    {values.permiteAgendamento && (
                                        <Grid item xs={12} sm={4}>
                                            <MDInput 
                                                fullWidth 
                                                type="number" 
                                                name="prazoMaxAgendamento" 
                                                label="Prazo Máx. Padrão (Dias Úteis)" 
                                                variant="standard" 
                                                value={values.prazoMaxAgendamento || 0} 
                                                onChange={handleChange} 
                                                required 
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={12}>
                                        <Autocomplete
                                            multiple
                                            options={grupos}
                                            getOptionLabel={(option) => option.nome || option.ccNome || option.id}
                                            value={selectedGrupos}
                                            onChange={(event, newValue) => {
                                                const newIds = newValue.map(v => v.id);
                                                setFieldValue("equipesIds", newIds);
                                                if (values.prazosEquipes) {
                                                    const updatedPrazos = { ...values.prazosEquipes };
                                                    Object.keys(updatedPrazos).forEach(k => {
                                                        if (!newIds.includes(k)) {
                                                            delete updatedPrazos[k];
                                                        }
                                                    });
                                                    setFieldValue("prazosEquipes", updatedPrazos);
                                                }
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Grupos com Acesso (Deixe vazio para Todos/Apenas Admins)" variant="standard" placeholder="Selecione..." />}
                                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        />
                                    </Grid>

                                    {showEquipesPrazos && (
                                        <Grid item xs={12}>
                                            <MDBox 
                                                p={2.5} 
                                                borderRadius="lg" 
                                                sx={{ 
                                                    backgroundColor: darkMode ? "#424550" : "#f8f9fa",
                                                    border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)"}`
                                                }}
                                            >
                                                <MDBox mb={1} display="flex" alignItems="center" gap={1}>
                                                    <Icon fontSize="small" color="info">schedule</Icon>
                                                    <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                        Prazos de Agendamento por Equipe
                                                    </MDTypography>
                                                </MDBox>
                                                <MDTypography variant="caption" color={darkMode ? "white" : "secondary"} display="block" mb={2}>
                                                    Defina o prazo de agendamento específico para cada equipe vinculada. Se o campo for deixado em branco ou zerado, a equipe herdará o <strong>Prazo Padrão de {values.prazoMaxAgendamento || 0} dias úteis</strong>.
                                                </MDTypography>

                                                {/* Cabeçalho da Lista */}
                                                <Grid container spacing={2} alignItems="center" sx={{ px: 1, pb: 1 }}>
                                                    <Grid item xs={12} sm={5}>
                                                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                            Equipe
                                                        </MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                            Prazo Customizado
                                                        </MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                            Prazo Vigente
                                                        </MDTypography>
                                                    </Grid>
                                                </Grid>

                                                <MDBox 
                                                    sx={{ 
                                                        height: "1px", 
                                                        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)",
                                                        mb: 1
                                                    }} 
                                                />

                                                {/* Linhas de Cada Equipe */}
                                                {selectedGrupos.map((g) => {
                                                    const customValue = values.prazosEquipes?.[g.id];
                                                    const hasCustom = customValue !== undefined && customValue !== null && customValue !== "" && Number(customValue) > 0;
                                                    const activeDays = hasCustom ? Number(customValue) : (values.prazoMaxAgendamento || 0);

                                                    return (
                                                        <Grid 
                                                            container 
                                                            spacing={2} 
                                                            alignItems="center" 
                                                            key={g.id}
                                                            sx={{ 
                                                                px: 1, 
                                                                py: 1.5,
                                                                borderBottom: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`
                                                            }}
                                                        >
                                                            <Grid item xs={12} sm={5}>
                                                                <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "text"}>
                                                                    {g.nome || g.ccNome || g.id}
                                                                </MDTypography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}>
                                                                <MDInput
                                                                    type="number"
                                                                    variant="standard"
                                                                    label="Dias Úteis"
                                                                    placeholder={`Padrão (${values.prazoMaxAgendamento || 0})`}
                                                                    value={customValue ?? ""}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value === "" ? "" : Number(e.target.value);
                                                                        setFieldValue("prazosEquipes", {
                                                                            ...values.prazosEquipes,
                                                                            [g.id]: val
                                                                        });
                                                                    }}
                                                                    inputProps={{ min: 0 }}
                                                                    sx={{ maxWidth: 180 }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={3}>
                                                                {hasCustom ? (
                                                                    <Chip 
                                                                        label={`${activeDays} dias úteis`} 
                                                                        size="small" 
                                                                        color="info" 
                                                                        variant="filled" 
                                                                    />
                                                                ) : (
                                                                    <Chip 
                                                                        label={`Padrão (${activeDays} dias)`} 
                                                                        size="small" 
                                                                        variant="outlined" 
                                                                        sx={{ 
                                                                            color: darkMode ? "#e0e0e0" : "text.secondary", 
                                                                            borderColor: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)" 
                                                                        }}
                                                                    />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                })}
                                            </MDBox>
                                        </Grid>
                                    )}

                                    <Grid item xs={12} sm={12}>
                                        <Autocomplete
                                            multiple
                                            options={statuses}
                                            getOptionLabel={(option) => option.ccNome || option.nome || option.id}
                                            value={statuses.filter(s => (values.statusFichaIds || []).includes(s.id))}
                                            onChange={(event, newValue) => {
                                                setFieldValue("statusFichaIds", newValue.map(v => v.id));
                                            }}
                                            renderInput={(params) => <MDInput {...params} label="Status Vinculados (Deixe vazio para Todos)" variant="standard" placeholder="Selecione..." />}
                                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        />
                                    </Grid>
                                </Grid>

                                <MDBox display="flex" justifyContent="flex-end" mt={4}>
                                    <MDButton variant="outlined" color="secondary" onClick={closeCreateEdit} sx={{ mr: 2 }}>Cancelar</MDButton>
                                    <MDButton variant="contained" color="info" type="submit" disabled={isSubmitting}>Salvar</MDButton>
                                </MDBox>
                            </Form>
                        );
                    }}
                </Formik>
            </MDBox>
        </Card>
    );
}
